import React, { useCallback } from 'react';
import { Collapse, Spin } from 'antd';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import DebouncedNumberInput from 'components/atoms/DebouncedNumberInput';
import DatePicker from 'components/molecules/common/Form/DatePicker';
import dayjs from 'dayjs';
import { sendNotification } from 'utils/helperMethods';
import ConfigDrivenFormFields from 'components/organism/TPOGen/components/ConfigDrivenFormFields';
import type { UseDimOptionsHook } from 'components/organism/TPOGen/components/DimSelect';
import type { DimSelectFieldConfig, FormFieldConfig } from 'components/organism/TPOGen/config/eventsConfig';
import { useTpoGenConfig } from 'components/organism/TPOGen/context/CustomerConfigContext';
import type { FormData } from './types';
import type { ObjectiveOptionType } from '../../hooks/queries/useObjectiveOptions';

const COLLAPSE_STYLE: React.CSSProperties = { backgroundColor: '#FFF' };

interface ScopeTabProps {
    scopeOfOptimisationFields: DimSelectFieldConfig[];
    baseInputFields: FormFieldConfig[];
    useDimOptions: UseDimOptionsHook;
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
    objectiveList: ObjectiveOptionType[];
    objectiveListLoading: boolean;
    objectiveListError: boolean;
    promotionTotalCount: number;
    promotionCountLoading?: boolean;
}

const ScopeTab: React.FC<ScopeTabProps> = ({
    scopeOfOptimisationFields,
    baseInputFields,
    useDimOptions,
    formData,
    setFormData,
    objectiveList,
    objectiveListLoading,
    objectiveListError,
    promotionTotalCount,
    promotionCountLoading = false,
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const handleKeepTopChange = useCallback(
        (val: number | null) => {
            setFormData((prev) => ({ ...prev, keep_top: val }));
        },
        [setFormData],
    );

    const handleKeepTopExceedMax = useCallback(() => {
        sendNotification('error', `Keep top value must be less than or equal to ${promotionTotalCount}`);
    }, [promotionTotalCount]);

    const renderBaseInputField = (field: FormFieldConfig) => {
        switch (field.type) {
            case 'select':
                return (
                    <div key={field.name} className="flex flex-col items-start">
                        <label className="text-sm text-[#18181b] pb-2 leading-[22px] flex items-center gap-1">
                            {field.required && <span className="text-redTriangle">* </span>}
                            {field.label}:
                        </label>
                        <SelectWithInfiniteScroller
                            hasMore={false}
                            showSearch={false}
                            filterOption={false}
                            placeholder={
                                objectiveListLoading
                                    ? tpoGenConfig.labels.optimizePlan.loadingPlaceholder
                                    : field.placeholder
                            }
                            maxTagCount="responsive"
                            className="!h-8 !w-full"
                            showArrow
                            loading={objectiveListLoading}
                            disabled={objectiveListLoading}
                            onChange={(val: string) => {
                                setFormData((prev) => ({ ...prev, [field.name!]: val }));
                            }}
                            value={formData[field.name!]}
                            options={objectiveListError ? [] : objectiveList}
                        />
                    </div>
                );

            case 'datepicker':
                return (
                    <div key={field.name} className="flex flex-col items-start">
                        <label className="text-sm text-[#18181b] pb-2 leading-[22px]">
                            {field.required && <span className="text-redTriangle">* </span>}
                            {field.label}:
                        </label>
                        <div className="w-full">
                            <DatePicker
                                value={formData[field.name!] as number | null}
                                label={null}
                                onChange={(date: number | null) => {
                                    setFormData((prev) => ({ ...prev, [field.name!]: date }));
                                }}
                                format="MM/DD/YYYY"
                                showToday={true}
                                className="!h-8 !w-full"
                                allowedDays={field.allowedDays}
                                enabledDates={
                                    field.disablePastDates
                                        ? { start: dayjs().startOf('day').unix() }
                                        : undefined
                                }
                            />
                        </div>
                    </div>
                );

            case 'numberWithContext': {
                return (
                    <div key={field.name} className="flex flex-col items-start">
                        <label className="text-sm text-[#18181b] pb-2 leading-[22px]">
                            {field.required && <span className="text-redTriangle">* </span>}
                            {field.label}{' '}
                            <span className="inline-flex items-center align-middle">
                                {promotionCountLoading ? <Spin size="small" /> : promotionTotalCount}
                            </span>{' '}
                            {field.contextSuffix}
                        </label>
                        <DebouncedNumberInput
                            value={formData[field.name!] as number | null}
                            min={0}
                            max={promotionTotalCount}
                            placeholder={field.placeholder}
                            onChange={handleKeepTopChange}
                            onExceedMax={handleKeepTopExceedMax}
                        />
                    </div>
                );
            }

            case 'numberInput':
                return (
                    <div key={field.name} className="flex flex-col items-start">
                        <label className="text-sm text-[#18181b] pb-2 leading-[22px]">
                            {field.required && <span className="text-redTriangle">* </span>}
                            {field.label}:
                        </label>
                        <DebouncedNumberInput
                            value={formData[field.name!] as number | null}
                            min={field.min ?? 0}
                            max={field.max}
                            placeholder={field.placeholder}
                            onChange={(val) => {
                                setFormData((prev) => ({ ...prev, [field.name!]: val }));
                            }}
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="h-full flex flex-col gap-2 overflow-y-auto">
            {/* Scope of Optimisation – uses ConfigDrivenFormFields (dim1 brand / sub-brand) */}
            <Collapse expandIconPosition="end" defaultActiveKey={['scope']} style={COLLAPSE_STYLE}>
                <Collapse.Panel
                    header={
                        <p className="text-sm font-semibold text-[#140b1d]">
                            {tpoGenConfig.labels.optimizePlan.scopeSection}
                        </p>
                    }
                    key="scope"
                >
                    <ConfigDrivenFormFields
                        fields={scopeOfOptimisationFields as FormFieldConfig[]}
                        useDimOptions={useDimOptions}
                        fieldClassName="w-[248px]"
                    />
                </Collapse.Panel>
            </Collapse>

            {/* Base Inputs – self-developed custom rendering */}
            <Collapse expandIconPosition="end" defaultActiveKey={['base']} style={COLLAPSE_STYLE}>
                <Collapse.Panel
                    header={
                        <p className="text-sm font-semibold text-[#140b1d]">
                            {tpoGenConfig.labels.optimizePlan.baseInputsSection}
                        </p>
                    }
                    key="base"
                >
                    <div className="grid grid-cols-3 gap-x-[26px] gap-y-4 px-1">
                        {baseInputFields.map(renderBaseInputField)}
                    </div>
                </Collapse.Panel>
            </Collapse>
        </div>
    );
};

export default React.memo(ScopeTab);
